import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";

interface ProductData {
  title: string;
  description: string;
  longDescription: string;
  features: string[];
  images: string[];
}

interface ProductsContent {
  [key: string]: ProductData;
}

const WoodProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content: { ar: { backToProducts: string; contactUs: string; features: string; ourProjects: string; products: ProductsContent }; en: { backToProducts: string; contactUs: string; features: string; ourProjects: string; products: ProductsContent } } = {
    ar: {
      backToProducts: "العودة للمنتجات الخشبية",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "home-furniture": {
          title: "أثاث منزلي",
          description: "أثاث منزلي فاخر بتصميمات عصرية تجمع بين الراحة والأناقة",
          longDescription: "نقدم تشكيلة واسعة من الأثاث المنزلي الفاخر المصنوع من أجود أنواع الأخشاب الطبيعية. تتميز منتجاتنا بالجودة العالية والتصاميم العصرية التي تناسب مختلف الأذواق والمساحات. من غرف المعيشة إلى غرف النوم، نقدم حلولاً متكاملة تجمع بين الراحة والأناقة.",
          features: ["تصاميم عصرية وكلاسيكية", "أخشاب طبيعية عالية الجودة", "تشطيبات متعددة الألوان", "ضمان شامل على المنتجات", "خدمة توصيل وتركيب"],
          images: ["/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png", "/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png"]
        },
        "wooden-doors": {
          title: "أبواب خشبية",
          description: "أبواب خشبية بجودة عالية مصممة لتدوم طويلاً",
          longDescription: "نوفر مجموعة متنوعة من الأبواب الخشبية الداخلية والخارجية بتصاميم كلاسيكية وعصرية. تتميز أبوابنا بالمتانة والجمال، مع خيارات متعددة من الأخشاب والتشطيبات لتناسب جميع احتياجاتكم.",
          features: ["أبواب داخلية وخارجية", "تصاميم كلاسيكية وعصرية", "عزل صوتي وحراري", "إكسسوارات عالية الجودة", "تركيب احترافي"],
          images: ["/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png", "/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png"]
        },
        "wooden-decorations": {
          title: "ديكورات خشبية",
          description: "ديكورات خشبية مميزة للمنازل تضفي لمسة أناقة",
          longDescription: "نقدم تشكيلة متنوعة من الديكورات الخشبية التي تضفي لمسة من الأناقة والدفء على مساحاتكم. من الرفوف الأنيقة إلى الحواجز الخشبية والأعمدة المزخرفة، نوفر حلولاً ديكورية متميزة.",
          features: ["رفوف وحوامل أنيقة", "حواجز وفواصل خشبية", "أعمدة وتيجان مزخرفة", "إطارات ولوحات خشبية", "قطع فنية مخصصة"],
          images: ["/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png", "/lovable-uploads/56f722da-bf52-4267-86b5-91b9db704f2b.png", "/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png"]
        },
        "restaurant-equipment": {
          title: "تجهيزات مطاعم",
          description: "تجهيزات خشبية للمطاعم والكافيهات",
          longDescription: "نقدم تجهيزات خشبية متكاملة للمطاعم والكافيهات تجمع بين الأناقة والمتانة. طاولات وكراسي وكاونترات وديكورات خشبية مصممة خصيصاً لتحمل الاستخدام المكثف مع الحفاظ على المظهر الراقي.",
          features: ["طاولات وكراسي متينة", "كاونترات وبارات", "ديكورات داخلية", "تصاميم مخصصة", "صيانة دورية"],
          images: ["/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png", "/lovable-uploads/office-furniture.jpg", "/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png"]
        },
        "office-furniture": {
          title: "أثاث مكتبي",
          description: "أثاث مكتبي خشبي عملي وأنيق",
          longDescription: "نوفر أثاثاً مكتبياً خشبياً عالي الجودة يجمع بين العملية والأناقة. مكاتب وكراسي وخزائن وطاولات اجتماعات مصممة بعناية لتوفير بيئة عمل مريحة ومنتجة.",
          features: ["مكاتب تنفيذية", "طاولات اجتماعات", "خزائن وأرفف", "كراسي مريحة", "حلول تخزين"],
          images: ["/lovable-uploads/office-furniture.jpg", "/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png"]
        },
        "wall-cladding": {
          title: "تجليد الجدران",
          description: "تجليد جدران خشبي بتصميمات فاخرة",
          longDescription: "نقدم حلول تجليد الجدران الخشبية الفاخرة التي تمنح المساحات طابعاً مميزاً من الدفء والفخامة. تشكيلة واسعة من الأنماط والتشطيبات لتناسب جميع الأذواق.",
          features: ["ألواح خشبية طبيعية", "تصاميم ثلاثية الأبعاد", "عزل صوتي وحراري", "سهولة الصيانة", "تركيب سريع"],
          images: ["/lovable-uploads/56f722da-bf52-4267-86b5-91b9db704f2b.png", "/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png"]
        },
        "hotel-equipment": {
          title: "تجهيز فنادق",
          description: "تجهيزات خشبية متكاملة للفنادق",
          longDescription: "نوفر تجهيزات خشبية متكاملة للفنادق تجمع بين الفخامة والمتانة. من أثاث الغرف إلى ديكورات الاستقبال والمطاعم، نقدم حلولاً شاملة تلبي أعلى معايير الضيافة.",
          features: ["أثاث غرف النزلاء", "ديكورات الاستقبال", "تجهيزات المطاعم", "أثاث المناطق المشتركة", "صيانة شاملة"],
          images: ["/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png", "/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png"]
        },
        "furnished-apartments": {
          title: "تجهيز شقق مفروشة",
          description: "أثاث خشبي للشقق المفروشة بأعلى جودة",
          longDescription: "نقدم حلول أثاث متكاملة للشقق المفروشة تجمع بين الأناقة والعملية والمتانة. تصاميم عصرية ومواد عالية الجودة تضمن رضا المستأجرين وعمراً طويلاً للأثاث.",
          features: ["أثاث غرف النوم", "أثاث غرف المعيشة", "تجهيزات المطبخ", "قطع ديكورية", "حزم متكاملة"],
          images: ["/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png", "/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png"]
        },
        "workspace-equipment": {
          title: "تجهيز مساحات العمل",
          description: "تجهيزات خشبية مخصصة لمساحات العمل المشتركة",
          longDescription: "نوفر تجهيزات خشبية متميزة لمساحات العمل المشتركة تجمع بين الوظيفة والجمالية. حلول مرنة ومبتكرة تدعم بيئات العمل الحديثة والتعاونية.",
          features: ["مكاتب مشتركة", "غرف اجتماعات", "مناطق استراحة", "حواجز ذكية", "حلول صوتية"],
          images: ["/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/office-furniture.jpg", "/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png"]
        },
        "wardrobes": {
          title: "خزائن الملابس",
          description: "خزائن ملابس خشبية بتصاميم عصرية",
          longDescription: "نقدم خزائن ملابس خشبية بتصاميم عصرية وتقسيمات داخلية مدروسة توفر أقصى استفادة من المساحة. حلول تخزين ذكية ومتعددة الخيارات تناسب جميع الاحتياجات.",
          features: ["تقسيمات داخلية ذكية", "أبواب متعددة الأنماط", "إضاءة داخلية", "إكسسوارات متكاملة", "تصاميم مخصصة"],
          images: ["/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png", "/lovable-uploads/435dc714-885d-4a20-99a7-01faffd3c1ad.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png"]
        },
        "kitchen-cabinets": {
          title: "خزائن المطبخ",
          description: "خزائن مطبخ خشبية تجمع بين الجودة والتصميم العملي",
          longDescription: "نوفر خزائن مطبخ خشبية عالية الجودة بتصاميم عملية وأنيقة. مواد مقاومة للرطوبة والحرارة مع تشطيبات متعددة لتناسب جميع أنماط المطابخ.",
          features: ["مواد مقاومة للرطوبة", "تصاميم عملية", "أدراج ورفوف ذكية", "مقابض وإكسسوارات فاخرة", "ضمان شامل"],
          images: ["/lovable-uploads/435dc714-885d-4a20-99a7-01faffd3c1ad.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png", "/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png"]
        }
      }
    },
    en: {
      backToProducts: "Back to Wood Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "home-furniture": {
          title: "Home Furniture",
          description: "Luxury home furniture with modern designs combining comfort and elegance",
          longDescription: "We offer a wide selection of luxury home furniture made from the finest natural woods. Our products feature high quality and modern designs that suit different tastes and spaces. From living rooms to bedrooms, we provide complete solutions combining comfort and elegance.",
          features: ["Modern and classic designs", "High-quality natural wood", "Multi-color finishes", "Comprehensive product warranty", "Delivery and installation service"],
          images: ["/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png", "/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png"]
        },
        "wooden-doors": {
          title: "Wooden Doors",
          description: "High-quality wooden doors designed to last long",
          longDescription: "We provide a variety of interior and exterior wooden doors with classic and modern designs. Our doors feature durability and beauty, with multiple wood and finish options to suit all your needs.",
          features: ["Interior and exterior doors", "Classic and modern designs", "Sound and thermal insulation", "High-quality accessories", "Professional installation"],
          images: ["/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png", "/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png"]
        },
        "wooden-decorations": {
          title: "Wooden Decorations",
          description: "Distinctive wooden decorations for homes adding elegance",
          longDescription: "We offer a varied selection of wooden decorations that add a touch of elegance and warmth to your spaces. From elegant shelves to wooden partitions and decorated columns, we provide distinguished decorative solutions.",
          features: ["Elegant shelves and holders", "Wooden partitions and dividers", "Decorated columns and capitals", "Wooden frames and panels", "Custom art pieces"],
          images: ["/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png", "/lovable-uploads/56f722da-bf52-4267-86b5-91b9db704f2b.png", "/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png"]
        },
        "restaurant-equipment": {
          title: "Restaurant Equipment",
          description: "Wooden equipment for restaurants and cafes",
          longDescription: "We provide complete wooden equipment for restaurants and cafes combining elegance and durability. Tables, chairs, counters, and wooden decorations specially designed to withstand intensive use while maintaining an elegant appearance.",
          features: ["Durable tables and chairs", "Counters and bars", "Interior decorations", "Custom designs", "Regular maintenance"],
          images: ["/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png", "/lovable-uploads/office-furniture.jpg", "/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png"]
        },
        "office-furniture": {
          title: "Office Furniture",
          description: "Practical and elegant wooden office furniture",
          longDescription: "We provide high-quality wooden office furniture combining practicality and elegance. Desks, chairs, cabinets, and meeting tables carefully designed to provide a comfortable and productive work environment.",
          features: ["Executive desks", "Meeting tables", "Cabinets and shelves", "Comfortable chairs", "Storage solutions"],
          images: ["/lovable-uploads/office-furniture.jpg", "/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png"]
        },
        "wall-cladding": {
          title: "Wall Cladding",
          description: "Luxury wooden wall cladding",
          longDescription: "We offer luxury wooden wall cladding solutions that give spaces a distinctive character of warmth and luxury. A wide selection of patterns and finishes to suit all tastes.",
          features: ["Natural wooden panels", "3D designs", "Sound and thermal insulation", "Easy maintenance", "Quick installation"],
          images: ["/lovable-uploads/56f722da-bf52-4267-86b5-91b9db704f2b.png", "/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png"]
        },
        "hotel-equipment": {
          title: "Hotel Equipment",
          description: "Complete wooden equipment for hotels",
          longDescription: "We provide complete wooden equipment for hotels combining luxury and durability. From room furniture to reception and restaurant decorations, we offer comprehensive solutions meeting the highest hospitality standards.",
          features: ["Guest room furniture", "Reception decorations", "Restaurant equipment", "Common area furniture", "Comprehensive maintenance"],
          images: ["/lovable-uploads/b007d6ed-6308-4e65-9c92-d100b12861ff.png", "/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png"]
        },
        "furnished-apartments": {
          title: "Furnished Apartments",
          description: "High-quality wooden furniture for furnished apartments",
          longDescription: "We offer complete furniture solutions for furnished apartments combining elegance, practicality, and durability. Modern designs and high-quality materials ensure tenant satisfaction and long furniture life.",
          features: ["Bedroom furniture", "Living room furniture", "Kitchen equipment", "Decorative pieces", "Complete packages"],
          images: ["/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png", "/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/446e75d8-cedf-4868-b00d-34f03c736054.png"]
        },
        "workspace-equipment": {
          title: "Workspace Equipment",
          description: "Custom wooden equipment for shared workspaces",
          longDescription: "We provide distinguished wooden equipment for shared workspaces combining function and aesthetics. Flexible and innovative solutions supporting modern and collaborative work environments.",
          features: ["Shared desks", "Meeting rooms", "Rest areas", "Smart partitions", "Acoustic solutions"],
          images: ["/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png", "/lovable-uploads/office-furniture.jpg", "/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png"]
        },
        "wardrobes": {
          title: "Wardrobes",
          description: "Modern wooden wardrobes",
          longDescription: "We offer wooden wardrobes with modern designs and thoughtful internal divisions providing maximum space utilization. Smart and multi-option storage solutions to suit all needs.",
          features: ["Smart internal divisions", "Multi-style doors", "Internal lighting", "Complete accessories", "Custom designs"],
          images: ["/lovable-uploads/61e6df14-89e7-4f12-a903-8761e6ddc560.png", "/lovable-uploads/435dc714-885d-4a20-99a7-01faffd3c1ad.png", "/lovable-uploads/f880eff4-fd91-451b-a6e6-3d0c6fabe56f.png"]
        },
        "kitchen-cabinets": {
          title: "Kitchen Cabinets",
          description: "Wooden kitchen cabinets combining quality and practical design",
          longDescription: "We provide high-quality wooden kitchen cabinets with practical and elegant designs. Moisture and heat resistant materials with multiple finishes to suit all kitchen styles.",
          features: ["Moisture resistant materials", "Practical designs", "Smart drawers and shelves", "Luxury handles and accessories", "Comprehensive warranty"],
          images: ["/lovable-uploads/435dc714-885d-4a20-99a7-01faffd3c1ad.png", "/lovable-uploads/c50756ad-e98d-4976-b622-c35dc59fa0d6.png", "/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png"]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/wood">
            <Button className="bg-wood hover:bg-wood/90">
              {t.backToProducts}
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Link to="/wood" className="inline-flex items-center gap-2 text-wood hover:text-wood/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-kufi font-bold text-wood mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        {/* Description Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl shadow-lg p-8 mb-12"
        >
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-wood/5 rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-kufi font-bold text-wood mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-wood flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Projects Gallery Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-kufi font-bold text-wood mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]"
              >
                <img
                  src={image}
                  alt={`${product.title} ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center bg-wood/10 rounded-xl p-8"
        >
          <Link to="/contact">
            <Button size="lg" className="bg-wood hover:bg-wood/90 text-white font-kufi text-lg px-8">
              {t.contactUs}
            </Button>
          </Link>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default WoodProductDetail;
